import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Save, X, Trophy, Calendar, Target, Edit, Trash2 } from 'lucide-react';
import './MatchGrouping.css';

function MatchGrouping({ token, nightMode, onSelectGroup, onBack }) {
  const [matchGroups, setMatchGroups] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    type: 'League',
    teamA: '',
    teamB: '',
    bestOf: 5,
    note: '',
    organization: ''
  });
  const [editGroup, setEditGroup] = useState({
    name: '',
    type: 'League',
    teamA: '',
    teamB: '',
    bestOf: 5,
    note: '',
    organization: ''
  });
  const [teamASuggestions, setTeamASuggestions] = useState([]);
  const [teamBSuggestions, setTeamBSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [organizationManuallyEdited, setOrganizationManuallyEdited] = useState(false);

  const matchTypes = ['League', 'Tournament', 'Scrimmage', 'School'];
  const bestOfOptions = [1, 3, 5, 7];

  useEffect(() => {
    fetchMatchGroups();
  }, []);

  const fetchMatchGroups = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/match-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchGroups(response.data.matchGroups || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch match groups');
      setLoading(false);
    }
  };

  // Helper function to auto-generate match name
  const generateMatchName = (teamA, teamB) => {
    if (teamA.trim() && teamB.trim()) {
      return `${teamA.trim()} vs ${teamB.trim()}`;
    }
    return '';
  };

  // Helper function to update match name and organization automatically
  const updateAutoFields = (updatedGroup) => {
    const updates = { ...updatedGroup };
    
    // Auto-generate match name if it's empty or was auto-generated
    const currentAutoName = generateMatchName(newGroup.teamA, newGroup.teamB);
    if (!newGroup.name.trim() || newGroup.name === currentAutoName) {
      updates.name = generateMatchName(updatedGroup.teamA, updatedGroup.teamB);
    }
    
    // Auto-populate organization with Team A name unless user has manually edited it
    if (!organizationManuallyEdited) {
      updates.organization = updatedGroup.teamA.trim();
    }
    
    return updates;
  };

  const fetchTeamSuggestions = async (query, field) => {
    if (!query || query.length < 2) {
      if (field === 'teamA') {
        setTeamASuggestions([]);
      } else {
        setTeamBSuggestions([]);
      }
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/matches/suggestions/teams?q=${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (field === 'teamA') {
        setTeamASuggestions(response.data);
      } else {
        setTeamBSuggestions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team suggestions:', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim() || !newGroup.teamA.trim() || !newGroup.teamB.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Ensure all fields are properly defined
      const createData = {
        name: newGroup.name,
        type: newGroup.type,
        teamA: newGroup.teamA,
        teamB: newGroup.teamB,
        bestOf: newGroup.bestOf,
        note: newGroup.note || '',
        organization: newGroup.organization || ''
      };
      
      console.log('Creating match group with data:', createData);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/match-groups`,
        createData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatchGroups([response.data, ...matchGroups]);
      setNewGroup({
        name: '',
        type: 'League',
        teamA: '',
        teamB: '',
        bestOf: 5,
        note: '',
        organization: ''
      });
      setOrganizationManuallyEdited(false);
      setShowCreateForm(false);
      setError('');
    } catch (error) {
      console.error('Create error:', error.response?.data || error.message);
      setError(`Failed to create match group: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group._id);
    setEditGroup({
      name: group.name,
      type: group.type,
      teamA: group.teamA,
      teamB: group.teamB,
      bestOf: group.bestOf,
      note: group.note || '',
      organization: group.organization || ''
    });
    setShowCreateForm(false);
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!editGroup.name.trim() || !editGroup.teamA.trim() || !editGroup.teamB.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Only send defined fields to avoid validation issues with undefined values
      const updateData = {
        name: editGroup.name,
        type: editGroup.type,
        teamA: editGroup.teamA,
        teamB: editGroup.teamB,
        bestOf: editGroup.bestOf,
        note: editGroup.note || '',
        organization: editGroup.organization || ''
      };
      
      console.log('Updating match group with data:', updateData);
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/match-groups/${editingGroup}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatchGroups(matchGroups.map(group => 
        group._id === editingGroup ? response.data : group
      ));
      setEditingGroup(null);
      setEditGroup({
        name: '',
        type: 'League',
        teamA: '',
        teamB: '',
        bestOf: 5,
        note: '',
        organization: ''
      });
      setError('');
    } catch (error) {
      console.error('Update error:', error.response?.data || error.message);
      setError(`Failed to update match group: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingGroup(null);
    setEditGroup({
      name: '',
      type: 'League',
      teamA: '',
      teamB: '',
      bestOf: 5,
      note: '',
      organization: ''
    });
    setTeamASuggestions([]);
    setTeamBSuggestions([]);
    setError('');
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this match group? This will also delete all associated matches.')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/match-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchGroups(matchGroups.filter(group => group._id !== groupId));
    } catch (error) {
      setError('Failed to delete match group');
    }
  };

  const getSeriesScore = (group) => {
    if (!group.matches || group.matches.length === 0) return { teamA: 0, teamB: 0 };
    
    let teamAWins = 0;
    let teamBWins = 0;
    
    group.matches.forEach(match => {
      if (match.scoreA > match.scoreB) {
        teamAWins++;
      } else if (match.scoreB > match.scoreA) {
        teamBWins++;
      }
    });
    
    return { teamA: teamAWins, teamB: teamBWins };
  };

  const getSeriesStatus = (group) => {
    const score = getSeriesScore(group);
    const winsNeeded = Math.ceil(group.bestOf / 2);
    
    if (score.teamA >= winsNeeded) return 'teamA';
    if (score.teamB >= winsNeeded) return 'teamB';
    return 'ongoing';
  };

  if (loading) {
    return (
      <div className={`match-grouping ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading">Loading match groups...</div>
      </div>
    );
  }

  return (
    <div className={`match-grouping ${nightMode ? 'night-mode' : ''}`}>
      <div className="match-grouping-header">
        <h1>Series</h1>
        <button 
          onClick={() => {
            setShowCreateForm(true);
            setOrganizationManuallyEdited(false);
          }} 
          className="create-group-btn"
        >
          <Plus size={16} />
          <span>New</span>
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <div className="create-group-form">
          <h3>New series</h3>
          <form onSubmit={handleCreateGroup}>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Match Name *</label>
                <input
                  type="text"
                  placeholder="e.g., ELCO vs Cocalico Varsity"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Team A *</label>
                <input
                  type="text"
                  placeholder="Team A name"
                  value={newGroup.teamA}
                  onChange={(e) => {
                    const updatedGroup = { ...newGroup, teamA: e.target.value };
                    const finalGroup = updateAutoFields(updatedGroup);
                    setNewGroup(finalGroup);
                    fetchTeamSuggestions(e.target.value, 'teamA');
                  }}
                  required
                />
                {teamASuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamASuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          const updatedGroup = { ...newGroup, teamA: suggestion };
                          const finalGroup = updateAutoFields(updatedGroup);
                          setNewGroup(finalGroup);
                          setTeamASuggestions([]);
                          // Reset manual edit flag since this is auto-population
                          setOrganizationManuallyEdited(false);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Team B *</label>
                <input
                  type="text"
                  placeholder="Team B name"
                  value={newGroup.teamB}
                  onChange={(e) => {
                    const updatedGroup = { ...newGroup, teamB: e.target.value };
                    const finalGroup = updateAutoFields(updatedGroup);
                    setNewGroup(finalGroup);
                    fetchTeamSuggestions(e.target.value, 'teamB');
                  }}
                  required
                />
                {teamBSuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamBSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          const updatedGroup = { ...newGroup, teamB: suggestion };
                          const finalGroup = updateAutoFields(updatedGroup);
                          setNewGroup(finalGroup);
                          setTeamBSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Best of *</label>
                <select
                  value={newGroup.bestOf}
                  onChange={(e) => setNewGroup({ ...newGroup, bestOf: parseInt(e.target.value) })}
                  required
                >
                  {bestOfOptions.map(option => (
                    <option key={option} value={option}>Best of {option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Match Type *</label>
                <select
                  value={newGroup.type}
                  onChange={(e) => setNewGroup({ ...newGroup, type: e.target.value })}
                  required
                >
                  {matchTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {newGroup.type && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>{newGroup.type} Name</label>
                  <input
                    type="text"
                    placeholder={`Enter ${newGroup.type.toLowerCase()} name (e.g., "${newGroup.type === 'League' ? 'Lancaster-Lebanon League' : newGroup.type === 'Tournament' ? 'State Championships' : newGroup.type === 'Scrimmage' ? 'Pre-season Scrimmage' : 'Ephrata High School'}")`}
                    value={newGroup.organization}
                    onChange={(e) => {
                      setNewGroup({ ...newGroup, organization: e.target.value });
                      setOrganizationManuallyEdited(true);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  placeholder="Optional notes about this match group..."
                  value={newGroup.note}
                  onChange={(e) => setNewGroup({ ...newGroup, note: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                <Save size={16} /> Create
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowCreateForm(false);
                  setOrganizationManuallyEdited(false);
                  setError('');
                  setTeamASuggestions([]);
                  setTeamBSuggestions([]);
                }} 
                className="cancel-btn"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {editingGroup && (
        <div className="create-group-form">
          <h3>Edit series</h3>
          <form onSubmit={handleUpdateGroup}>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Match Name *</label>
                <input
                  type="text"
                  placeholder="e.g., ELCO vs Cocalico Varsity"
                  value={editGroup.name}
                  onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Team A *</label>
                <input
                  type="text"
                  placeholder="Team A name"
                  value={editGroup.teamA}
                  onChange={(e) => {
                    setEditGroup({ ...editGroup, teamA: e.target.value });
                    fetchTeamSuggestions(e.target.value, 'teamA');
                  }}
                  required
                />
                {teamASuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamASuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          setEditGroup({ ...editGroup, teamA: suggestion });
                          setTeamASuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Team B *</label>
                <input
                  type="text"
                  placeholder="Team B name"
                  value={editGroup.teamB}
                  onChange={(e) => {
                    setEditGroup({ ...editGroup, teamB: e.target.value });
                    fetchTeamSuggestions(e.target.value, 'teamB');
                  }}
                  required
                />
                {teamBSuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamBSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => {
                          setEditGroup({ ...editGroup, teamB: suggestion });
                          setTeamBSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Best of *</label>
                <select
                  value={editGroup.bestOf}
                  onChange={(e) => setEditGroup({ ...editGroup, bestOf: parseInt(e.target.value) })}
                  required
                >
                  {bestOfOptions.map(option => (
                    <option key={option} value={option}>Best of {option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Match Type *</label>
                <select
                  value={editGroup.type}
                  onChange={(e) => setEditGroup({ ...editGroup, type: e.target.value })}
                  required
                >
                  {matchTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {editGroup.type && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>{editGroup.type} Name</label>
                  <input
                    type="text"
                    placeholder={`Enter ${editGroup.type.toLowerCase()} name (e.g., "${editGroup.type === 'League' ? 'Lancaster-Lebanon League' : editGroup.type === 'Tournament' ? 'State Championships' : editGroup.type === 'Scrimmage' ? 'Pre-season Scrimmage' : 'Ephrata High School'}")`}
                    value={editGroup.organization}
                    onChange={(e) => setEditGroup({ ...editGroup, organization: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  placeholder="Optional notes about this match group..."
                  value={editGroup.note}
                  onChange={(e) => setEditGroup({ ...editGroup, note: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                <Save size={16} /> Save
              </button>
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="cancel-btn"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="match-groups-grid">
        {matchGroups.map(group => {
          const seriesScore = getSeriesScore(group);
          const status = getSeriesStatus(group);
          const winsNeeded = Math.ceil(group.bestOf / 2);
          const autoName = `${group.teamA} vs ${group.teamB}`;
          const customName = group.name && group.name.trim() !== autoName ? group.name : null;
          
          return (
            <article key={group._id} className={`match-group-card ${status}`}>
              <div className="group-meta">
                <div className="group-meta-left">
                  <span className={`type-badge ${group.type.toLowerCase()}`}>
                    {group.type}
                  </span>
                  {group.organization && (
                    <span className="group-org" title={group.organization}>{group.organization}</span>
                  )}
                </div>
                <span className={`status-pill ${status === 'ongoing' ? 'live' : 'done'}`}>
                  {status === 'ongoing' ? 'Live' : 'Final'}
                </span>
              </div>

              {customName && <h3 className="group-name">{customName}</h3>}

              <div className="series-board">
                <div className={`series-row ${status === 'teamA' ? 'is-winner' : ''}`}>
                  <span className="series-team-name" title={group.teamA}>{group.teamA}</span>
                  <div className="series-pips" aria-hidden="true">
                    {Array.from({ length: winsNeeded }).map((_, i) => (
                      <span key={i} className={`pip ${i < seriesScore.teamA ? 'on' : ''}`} />
                    ))}
                  </div>
                  <span className="series-set-score">{seriesScore.teamA}</span>
                </div>
                <div className={`series-row ${status === 'teamB' ? 'is-winner' : ''}`}>
                  <span className="series-team-name" title={group.teamB}>{group.teamB}</span>
                  <div className="series-pips" aria-hidden="true">
                    {Array.from({ length: winsNeeded }).map((_, i) => (
                      <span key={i} className={`pip ${i < seriesScore.teamB ? 'on' : ''}`} />
                    ))}
                  </div>
                  <span className="series-set-score">{seriesScore.teamB}</span>
                </div>
              </div>

              {group.note && (
                <p className="group-note">{group.note}</p>
              )}

              <div className="group-footer">
                <span className="group-footer-meta">
                  <Target size={12} /> Best of {group.bestOf}
                  <span className="dot">·</span>
                  {group.matches?.length || 0} sets
                  <span className="dot">·</span>
                  <Calendar size={12} /> {new Date(group.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
                <div className="group-actions">
                  <button 
                    onClick={() => onSelectGroup(group)}
                    className="view-btn"
                  >
                    View
                  </button>
                  <button 
                    onClick={() => handleEditGroup(group)}
                    className="icon-btn edit-btn"
                    aria-label="Edit series"
                    title="Edit"
                  >
                    <Edit size={15} />
                  </button>
                  <button 
                    onClick={() => handleDeleteGroup(group._id)}
                    className="icon-btn delete-btn"
                    aria-label="Delete series"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {matchGroups.length === 0 && (
        <div className="empty-state">
          <Trophy size={36} />
          <p>No series yet. Create one to group sets for a matchup.</p>
        </div>
      )}
    </div>
  );
}

export default MatchGrouping;
